import { useState, useCallback } from 'react';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { notifications } from '@mantine/notifications';
import { groupsApi } from '../../../../services/api/groups';
import type { GroupWithMembers } from '../../../../services/api/groups';
import type { Participant } from '../../../../types';

interface UseParticipantCardDragDropOptions {
  cohortId: string;
  groups: GroupWithMembers[];
  onRefresh: () => void | Promise<void>;
  onParticipantAssigned: (userId: string) => void;
}

export interface UseParticipantCardDragDropResult {
  activeDragParticipant: Participant | null;
  handleParticipantDragStart: (event: DragStartEvent) => void;
  handleParticipantDragEnd: (event: DragEndEvent) => Promise<void>;
}

/**
 * Manages drag-and-drop state for dragging unassigned participants
 * from the side panel onto group assignment cards.
 */
export function useParticipantCardDragDrop({
  cohortId,
  groups,
  onRefresh,
  onParticipantAssigned,
}: UseParticipantCardDragDropOptions): UseParticipantCardDragDropResult {
  const [activeDragParticipant, setActiveDragParticipant] = useState<Participant | null>(null);

  const handleParticipantDragStart = useCallback((event: DragStartEvent) => {
    const participant = event.active.data.current?.participant as Participant | undefined;
    if (participant) {
      setActiveDragParticipant(participant);
    }
  }, []);

  const handleParticipantDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDragParticipant(null);

      if (!over) return;

      const participant = active.data.current?.participant as Participant | undefined;
      if (!participant) return;

      const dropId = String(over.id);
      if (!dropId.startsWith('coach-drop-group-')) return;

      const groupId = dropId.replace('coach-drop-group-', '');
      const targetGroup = groups.find((g) => g.id === groupId);
      if (!targetGroup) return;

      // Check if participant is already in this group
      const isAlreadyMember = targetGroup.members.some((m) => m.user_id === participant.user_id);
      if (isAlreadyMember) {
        notifications.show({
          title: 'Already Assigned',
          message: `${participant.first_name} ${participant.last_name} is already in ${targetGroup.name}`,
          color: 'blue',
        });
        return;
      }

      try {
        await groupsApi.assignMember(cohortId, groupId, participant.user_id);

        const name = `${participant.first_name || ''} ${participant.last_name || ''}`.trim();
        notifications.show({
          title: 'Participant Assigned',
          message: `${name} assigned to ${targetGroup.name}`,
          color: 'green',
        });

        onParticipantAssigned(participant.user_id);
        await onRefresh();
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to assign participant',
          color: 'red',
        });
      }
    },
    [cohortId, groups, onRefresh, onParticipantAssigned],
  );

  return {
    activeDragParticipant,
    handleParticipantDragStart,
    handleParticipantDragEnd,
  };
}
